import { logger } from "@origin-digital/reporting-client";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { IFetchAccessToken } from "@origin-digital/event-dispatcher";

import { auth0EnvConfigs } from "../../config";
import { IAuth0Config } from "../../interfaces";
import { decodeJwt, verifyJwt } from "../jwtUtils";
import { getCookie } from "../utils";
import { LOG_TAG } from "../../logTag";
import { getCachedJwtEntry } from "./cacheUtils";
import { OriginSpaAuthClient } from "./OriginSpaAuthClient";

const SAP_TOKEN_COOKIE = "user-token";
const SAP_TIMEOUT_COOKIE = "is-logged-in";

const handleFailedSilentAuth =
  (auth0Params: IAuth0Config) => async (e: Error) => {
    // These are temporary to help identify/debug the root cause of failed silent auth
    const hasCookie = !!getCookie(SAP_TOKEN_COOKIE);
    const sapTokenTimeout = getCookie(SAP_TIMEOUT_COOKIE);
    const isSapTokenExpired = sapTokenTimeout
      ? Date.now() > parseInt(sapTokenTimeout, 10)
      : true;

    const cachedJwt = getCachedJwtEntry(auth0Params);
    let isJwtValid = !!cachedJwt;

    try {
      if (cachedJwt) {
        const decoded = decodeJwt(cachedJwt.body.access_token);
        await verifyJwt(cachedJwt.body.access_token, decoded, auth0Params);
      }
    } catch (error) {
      isJwtValid = false;
      logger.error(`${LOG_TAG} Invalid JWT token`, error);
    }

    logger.error(`${LOG_TAG} Error getting jwt silently`, {
      ...e,
      hasCookie,
      isSapTokenExpired,
      hasJwt: !!cachedJwt,
      isJwtValid,
    });

    // TO-DO: Check for error code "login_required" and redirect to login page
    // SY-1629
    return Promise.reject(new Error("error getting jwt silently"));
  };

/**
 * Triggers Auth0 step-up by calling `loginWithRedirect` with `stepup=true`.
 * The tenant's `stepup=true` Auth0 Rule responds by requiring MFA and
 * granting elevated scopes; the redirect returns to `returnTo` with the
 * exchange code.
 *
 * No-ops if `additionalScopes` is empty.
 *
 * Used by `@origin-digital/mesh`'s `WebPlatformHandler.STEP_UP_AUTH` topic
 * to serve desktop-web microsites. Native microsites use `StepUpGuard`
 * from this package's `react/` subfolder which calls `loginWithRedirect`
 * directly.
 */
export const stepUpAuth = async (
  env: EnvironmentNames,
  returnTo: string,
  additionalScopes: string[]
): Promise<void> => {
  if (additionalScopes.length === 0) return;
  const auth0Params = auth0EnvConfigs[env];
  // eslint-disable-next-line no-new
  const auth0 = new OriginSpaAuthClient(auth0Params);
  await auth0.loginWithRedirect({
    redirectUri: returnTo,
    additionalParams: { stepup: "true" },
  });
};

/**
 * Silent JWT retrieval. Wraps `OriginSpaAuthClient.getJwtSilently()` with
 * structured failure logging (cookie state, JWT cache validity) so
 * recurring silent-auth failures can be diagnosed in production.
 *
 * Web-only — native microsites get their JWT via mesh's native channel
 * (`fetchAccessToken` → mobile shell → Keychain).
 */
export const getJwt = (env: EnvironmentNames): IFetchAccessToken["result"] => {
  const auth0Params = auth0EnvConfigs[env];
  if (!auth0Params) {
    return Promise.reject(
      new Error(`auth0 config missing for environment: ${env}`)
    );
  }
  const originSpaAuthClient = new OriginSpaAuthClient(auth0Params);

  return originSpaAuthClient
    .getJwtSilently()
    .then((accessToken) => ({ accessToken }))
    .catch(handleFailedSilentAuth(auth0Params));
};
